import React, { useState } from 'react';
import { ArrowUp, ArrowDown, X, Plus, Image as ImageIcon } from 'lucide-react';
import MediaPicker from '../../../components/media/MediaPicker';

/**
 * FieldInput — renders the appropriate input UI for any manifest/block field.
 * Extracted from WebsiteBuilder.js so the BlockEditPanel can reuse the same
 * inputs (text, textarea, richtext, image, color, toggle, select, list).
 */

function LocaleTabs({ active, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50 text-[11px]">
      {['ar', 'en'].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => onChange(l)}
          className={[
            'px-2 py-0.5 rounded font-medium transition-colors',
            active === l ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700',
          ].join(' ')}
        >
          {l === 'ar' ? 'العربية' : 'English'}
        </button>
      ))}
    </div>
  );
}

function TextInput({ field, value, onChange }) {
  const [locale, setLocale] = useState('ar');
  if (!field.localised) {
    return (
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
      />
    );
  }
  const normalised = (value && typeof value === 'object') ? value : { ar: '', en: '' };
  return (
    <div>
      <div className="flex justify-end mb-1"><LocaleTabs active={locale} onChange={setLocale} /></div>
      <input
        type="text"
        value={normalised[locale] || ''}
        onChange={(e) => onChange({ ...normalised, [locale]: e.target.value })}
        dir={locale === 'en' ? 'ltr' : 'rtl'}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
      />
    </div>
  );
}

function TextAreaInput({ field, value, onChange }) {
  const [locale, setLocale] = useState('ar');
  if (!field.localised) {
    return (
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
      />
    );
  }
  const normalised = (value && typeof value === 'object') ? value : { ar: '', en: '' };
  return (
    <div>
      <div className="flex justify-end mb-1"><LocaleTabs active={locale} onChange={setLocale} /></div>
      <textarea
        value={normalised[locale] || ''}
        onChange={(e) => onChange({ ...normalised, [locale]: e.target.value })}
        dir={locale === 'en' ? 'ltr' : 'rtl'}
        rows={field.type === 'richtext' ? 7 : 3}
        className={[
          'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
          field.type === 'richtext' ? 'font-mono' : '',
        ].join(' ')}
      />
      {field.type === 'richtext' && (
        <p className="text-[10px] text-gray-400 mt-0.5">HTML مسموح — استخدم &lt;p&gt;, &lt;strong&gt;, &lt;br&gt; إلخ</p>
      )}
    </div>
  );
}

function ImageInput({ field, value, onChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const url = (value && typeof value === 'object') ? (value.publicUrl || value.url || '') : value;
  return (
    <>
      {url ? (
        <div className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 bg-gray-50">
          <img src={url} alt="" className="w-14 h-14 rounded object-cover shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-gray-500 font-mono truncate" dir="ltr">{url}</div>
            <div className="flex items-center gap-1 mt-1">
              <button type="button" onClick={() => setPickerOpen(true)} className="text-[11px] text-blue-600 hover:underline">تغيير</button>
              <span className="text-gray-300">·</span>
              <button type="button" onClick={() => onChange('')} className="text-[11px] text-blue-600 hover:underline">إزالة</button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500 hover:border-blue-300 hover:bg-blue-50/40 transition-colors"
        >
          <ImageIcon className="w-4 h-4" />
          اختر صورة من المكتبة
        </button>
      )}
      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(asset) => { onChange(asset?.publicUrl || ''); setPickerOpen(false); }}
        folder={field.folder || 'sections'}
      />
    </>
  );
}

function ColorInput({ value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer" />
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} dir="ltr" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono" />
    </div>
  );
}

function ToggleInput({ field, value, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
      <span className="text-sm text-gray-700">{field.label}</span>
    </label>
  );
}

function SelectInput({ field, value, onChange }) {
  return (
    <select value={value ?? field.default ?? ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white">
      {(field.options || []).map((o) => (
        <option key={o.value} value={o.value}>{o.label || o.labelAr || o.value}</option>
      ))}
    </select>
  );
}

function ListInput({ field, value, onChange }) {
  const items = Array.isArray(value) ? value : (field.default || []);
  const setItem = (i, next) => onChange(items.map((it, idx) => (idx === i ? next : it)));
  const addItem = () => {
    const template = (field.itemFields || []).reduce((acc, f) => ({ ...acc, [f.key]: f.default ?? (f.localised ? { ar: '', en: '' } : '') }), {});
    onChange([...(items || []), template]);
  };
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const moveItem = (i, delta) => {
    const to = i + delta;
    if (to < 0 || to >= items.length) return;
    const next = items.slice();
    const [m] = next.splice(i, 1);
    next.splice(to, 0, m);
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {(items || []).map((item, i) => (
        <div key={i} className="p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-gray-500">#{i + 1}</span>
            <div className="flex items-center gap-0.5">
              <button type="button" onClick={() => moveItem(i, -1)} disabled={i === 0} className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-white disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-white disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => removeItem(i)} className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-white"><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          {(field.itemFields || []).map((sub) => (
            <div key={sub.key}>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">{sub.label}</label>
              <FieldInput field={sub} value={item?.[sub.key]} onChange={(v) => setItem(i, { ...item, [sub.key]: v })} />
            </div>
          ))}
        </div>
      ))}
      <button type="button" onClick={addItem} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-xs text-gray-600 hover:border-blue-300 hover:text-blue-700 transition-colors">
        <Plus className="w-3.5 h-3.5" />
        إضافة عنصر
      </button>
    </div>
  );
}

export default function FieldInput({ field, value, onChange }) {
  const common = { field, value, onChange };
  switch (field.type) {
    case 'text':      return <TextInput {...common} />;
    case 'textarea':  return <TextAreaInput {...common} />;
    case 'richtext':  return <TextAreaInput {...common} />;
    case 'image':     return <ImageInput {...common} />;
    case 'color':     return <ColorInput value={value ?? field.default} onChange={onChange} />;
    case 'toggle':    return <ToggleInput {...common} />;
    case 'select':    return <SelectInput {...common} />;
    case 'list':      return <ListInput {...common} />;
    default:          return <input type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />;
  }
}
